import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Typography,
} from '@mui/material';

/**
 * Prompt for enrichment defaults before starting a group.
 */
const GroupEnrichmentModal = ({ contextsUrlTemplate, updateUrlTemplate, models }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [contexts, setContexts] = useState([]);
    const [valuesByContext, setValuesByContext] = useState({});
    const [pendingLink, setPendingLink] = useState('');

    const modelNameById = useMemo(() => {
        const map = new Map();
        models.forEach((model) => {
            map.set(String(model.id), model.name);
        });
        return map;
    }, [models]);

    const buildValuesForContext = (context) => {
        const schemaFields = context?.ui_schema?.fields || [];
        const stored = context?.ui_values || {};
        const nextValues = {};

        schemaFields.forEach((field) => {
            const key = field?.key?.trim();
            if (!key) {
                return;
            }
            if (Object.prototype.hasOwnProperty.call(stored, key)) {
                nextValues[key] = stored[key];
            } else {
                nextValues[key] = field?.type === 'toggle' ? false : '';
            }
        });

        return nextValues;
    };

    const fetchContexts = async (groupName) => {
        if (!contextsUrlTemplate) {
            return [];
        }

        const url = contextsUrlTemplate.replace('__GROUP__', encodeURIComponent(groupName));
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) {
            throw new Error('Failed to load enrichment contexts');
        }
        const payload = await response.json();
        return payload.data || [];
    };

    const handleStartClick = async (event) => {
        if (!models.length) {
            return;
        }

        const trigger = event.currentTarget;
        const groupName = trigger.dataset.group || '';
        const href = trigger.getAttribute('href') || '';

        if (!groupName || !href) {
            return;
        }

        event.preventDefault();
        setError('');
        setLoading(true);

        try {
            const fetchedContexts = await fetchContexts(groupName);
            if (!fetchedContexts.length) {
                window.location.href = href;
                return;
            }

            const nextValues = {};
            fetchedContexts.forEach((context) => {
                nextValues[context.id] = buildValuesForContext(context);
            });

            setContexts(fetchedContexts);
            setValuesByContext(nextValues);
            setPendingLink(href);
            setOpen(true);
        } catch (loadError) {
            console.error(loadError);
            setError('Failed to load enrichment defaults.');
        } finally {
            setLoading(false);
        }
    };

    const handleValueChange = (contextId, fieldKey, value) => {
        setValuesByContext((prev) => ({
            ...prev,
            [contextId]: {
                ...(prev[contextId] || {}),
                [fieldKey]: value,
            },
        }));
    };

    const handleClose = () => {
        setOpen(false);
        setContexts([]);
        setValuesByContext({});
        setPendingLink('');
        setError('');
    };

    const handleSaveAndStart = async () => {
        if (!updateUrlTemplate) {
            setError('Update URL is not configured.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await Promise.all(
                contexts
                    .filter((context) => !context.read_only)
                    .map((context) => {
                    const url = updateUrlTemplate.replace('__ID__', context.id);
                    const payload = {
                        ui_values: valuesByContext[context.id] || {},
                    };

                    return fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/json',
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                        },
                        body: JSON.stringify(payload),
                    }).then((response) => {
                        if (!response.ok) {
                            throw new Error('Failed to save defaults');
                        }
                        return response.json();
                    });
                })
            );

            if (pendingLink) {
                window.location.href = pendingLink;
            } else {
                handleClose();
            }
        } catch (saveError) {
            console.error(saveError);
            setError('Failed to save defaults.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!models.length) {
            return undefined;
        }

        const startLinks = document.querySelectorAll('.scheduled-toggle, .instant-toggle');
        startLinks.forEach((link) => {
            link.addEventListener('click', handleStartClick);
        });

        return () => {
            startLinks.forEach((link) => {
                link.removeEventListener('click', handleStartClick);
            });
        };
    }, [models]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Confirm Enrichment Defaults</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Review the enrichment defaults before starting this group. These values will be applied to future analyzer rows.
                </Typography>
                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}
                <Box sx={{ display: 'grid', gap: 2 }}>
                    {contexts.map((context) => {
                        const schemaFields = context?.ui_schema?.fields || [];
                        const contextValues = valuesByContext[context.id] || {};
                        const modelName = context.model_id
                            ? modelNameById.get(String(context.model_id)) || `#${context.model_id}`
                            : 'All';
                        const isReadOnly = Boolean(context.read_only);

                        return (
                            <Paper key={context.id} variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    {context.group_name ? `Group: ${context.group_name}` : 'Group default'} · Model: {modelName}
                                </Typography>
                                {isReadOnly && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Locked by admin
                                    </Typography>
                                )}
                                {schemaFields.length === 0 ? (
                                    <Typography color="text.secondary">No fields configured for this context.</Typography>
                                ) : (
                                    <Grid container spacing={2}>
                                        {schemaFields.map((field, index) => {
                                            const fieldKey = field?.key?.trim() || '';
                                            if (!fieldKey) {
                                                return null;
                                            }

                                            if (field.type === 'toggle') {
                                                return (
                                                    <Grid item xs={12} md={6} key={`${context.id}-${fieldKey}-${index}`}>
                                                        <FormControlLabel
                                                            control={(
                                                                <Switch
                                                                    checked={Boolean(contextValues[fieldKey])}
                                                                    onChange={(event) => handleValueChange(context.id, fieldKey, event.target.checked)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            )}
                                                            label={field.label || fieldKey}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            True: {field.true_label || 'Yes'} / False: {field.false_label || 'No'}
                                                        </Typography>
                                                    </Grid>
                                                );
                                            }

                                            const options = (field.options || []).filter((option) => option.value?.trim());

                                            return (
                                                <Grid item xs={12} md={6} key={`${context.id}-${fieldKey}-${index}`}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel id={`context-${context.id}-${fieldKey}`}>{field.label || fieldKey}</InputLabel>
                                                        <Select
                                                            labelId={`context-${context.id}-${fieldKey}`}
                                                            value={contextValues[fieldKey] ?? ''}
                                                            label={field.label || fieldKey}
                                                            onChange={(event) => handleValueChange(context.id, fieldKey, event.target.value)}
                                                            disabled={isReadOnly}
                                                        >
                                                            {options.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label || option.value}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSaveAndStart} disabled={loading}>
                    {loading ? 'Saving...' : 'Save and Start'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GroupEnrichmentModal;
